import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { SchemesSearchDropdown } from "@transactions/components/schemes-search-dropdown";
import {
  Button,
  cn,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
} from "web-design-system";
import { AlertCircle, CheckCircle, PlusIcon, X } from "lucide-react";
import { useMemo, useRef } from "react";
import { useCreateFundBasket, useUpdateFundBasket } from "creso-core/hooks";
import { handleError } from "@/utils/error";
import { toast } from "sonner";
import { FundBasketFormValues } from "./dialog";

export function BasketFunds(props: { next: () => void }) {
  const { next } = props;
  const form = useFormContext<FundBasketFormValues>();

  const { fields, update, append, remove } = useFieldArray({
    control: form.control,
    name: "funds",
  });

  const { mutateAsync: createFundBasket } = useCreateFundBasket();

  const { mutateAsync: updateFundBasket } = useUpdateFundBasket();

  const handleSubmit = useMemo(
    () =>
      form.handleSubmit(async (values: FundBasketFormValues) => {
        try {
          if (!values.tenure || !values.strategy) {
            throw new Error("Tenure and strategy are required");
          }
          if (values.id) {
            await updateFundBasket({
              id: values.id,
              name: values.name,
              tenure: values.tenure,
              strategy: values.strategy,
              funds: values.funds,
            });
          } else {
            await createFundBasket({
              name: values.name,
              tenure: values.tenure,
              strategy: values.strategy,
              funds: values.funds,
            });
          }
          next();
        } catch (error) {
          handleError(error, "Failed to save fund basket");
        }
      }),
    [next, createFundBasket, updateFundBasket, form]
  );

  const allocationInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const watchedFunds = useWatch({ control: form.control, name: "funds" });
  const totalAllocation =
    watchedFunds?.reduce(
      (sum, f) => sum + (Number(f.allocation_percentage) || 0),
      0
    ) ?? 0;
  const remaining = 100 - totalAllocation;
  const isFullyAllocated = totalAllocation === 100;
  const isOverAllocated = totalAllocation > 100;

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="flex min-h-full flex-col">
        <div className="flex flex-1 flex-col gap-6 p-6">
          <span className="text-lg font-semibold">Pick Funds</span>
          <div className="space-y-4">
            {fields.map((field, index) => (
              <div className="grid grid-cols-12 gap-4" key={field.id}>
                <div className="col-span-8">
                  <SchemesSearchDropdown
                    value={
                      field.name
                        ? {
                            label: field.name,
                            value: field.fund_scheme.toString(),
                          }
                        : null
                    }
                    onChange={(value) => {
                      if (
                        fields.find(
                          (f) => f.fund_scheme === value?.fund_scheme_id
                        )
                      ) {
                        toast.warning("Fund already exists");
                        return;
                      }
                      update(index, {
                        name: value?.scheme_name ?? "",
                        fund_scheme: value?.fund_scheme_id ?? 0,
                        allocation_percentage: field.allocation_percentage,
                        provider_company_id: value?.amc_morningstar_code ?? "",
                      });
                      requestAnimationFrame(() => {
                        allocationInputRefs.current[index]?.focus();
                      });
                    }}
                    type="sip"
                    placeholder="Select Scheme"
                  />
                </div>
                <FormField
                  className="col-span-3"
                  control={form.control}
                  name={`funds.${index}.allocation_percentage`}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <div className="relative w-full">
                          <Input
                            {...field}
                            ref={(el) => {
                              allocationInputRefs.current[index] = el;
                            }}
                            type="number"
                            value={field.value?.toString() ?? ""}
                            onChange={(e) =>
                              field.onChange(
                                Math.min(
                                  100,
                                  Math.max(0, Number(e.target.value))
                                )
                              )
                            }
                            min={0}
                            max={100}
                            className="w-full pr-9 text-right"
                          />
                          <span
                            aria-hidden
                            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground"
                          >
                            %
                          </span>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="col-span-1">
                  <Button
                    onClick={() => remove(index)}
                    variant="ghost"
                    size="icon"
                  >
                    <X className="size-4" />
                  </Button>
                </div>
              </div>
            ))}
            <Button
              className={cn("w-fit")}
              variant="ghost"
              type="button"
              onClick={() =>
                append({
                  name: "",
                  fund_scheme: 0,
                  allocation_percentage: 0,
                })
              }
            >
              <PlusIcon className="mr-1 size-4" />
              Add New Scheme
            </Button>
          </div>
        </div>
        <div className="sticky bottom-0 z-10 flex shrink-0 items-center justify-between border-t border-border bg-background p-5">
          <div
            className={cn(
              "flex bg-muted border border-secondary items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg",
              isFullyAllocated
                ? "text-constructive"
                : isOverAllocated
                  ? "text-destructive"
                  : "text-muted-foreground"
            )}
          >
            {isFullyAllocated ? (
              <>
                <CheckCircle className="size-4" />
                100% allocated
              </>
            ) : isOverAllocated ? (
              <>
                <AlertCircle className="size-4" />
                {Math.abs(remaining).toFixed(2)}% extra allocated
              </>
            ) : (
              <>{remaining.toFixed(2)}% left to allocate</>
            )}
          </div>
          <Button disabled={!isFullyAllocated} onClick={handleSubmit}>
            Save Basket
          </Button>
        </div>
      </div>
    </div>
  );
}
